import { Clipboard } from '@capacitor/clipboard';
import { isPast } from 'date-fns';
import { useCallback, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router';

import ExpandableSection from '@components/animations/ExpandableSection';
import ActionIconButton from '@components/buttons/ActionIconButton';
import PrimaryButton from '@components/buttons/PrimaryButton';
import DateStamp from '@components/dates/DateStamp';
import TimeRange from '@components/dates/TimeRange';
import ExpertAvatar from '@components/ExpertAvatar';
import styles from '@components/expertSection/ExpertSection.module.css';
import ExpertRescheduleSession from '@components/expertSection/schedule/ExpertRescheduleSession';
import ConsultCancelForm from '@components/forms/ConsultCancelForm';
import ConsultMoveForm from '@components/forms/ConsultMoveForm';
import ConsultUpdateForm from '@components/forms/ConsultUpdateForm';
import NewJourneyForm from '@components/forms/NewJourneyForm';
import Modal, { ModalElement } from '@components/Modal';
import Nbsp from '@components/Nbsp';
import AlertPopover from '@components/popovers/AlertPopover';
import Icon from '@components/text/Icon';
import TextWithIcon from '@components/text/TextWithIcon';
import BodyCopy from '@components/typography/BodyCopy';
import BodyCopyBold from '@components/typography/BodyCopyBold';
import Hyperlink from '@components/typography/Hyperlink';
import useMyExpert from '@hooks/useMyExpert';
import useOpener from '@hooks/useOpener';
import useSanitizeHtml from '@hooks/useSanitizeHtml';
import { ContactMethodEnum, GetJourneyWithExpertAndConsultsQuery } from '@typing/Generated';
import { clickCurrentTarget } from '@utils/keyboardUtils';
import { languageNameForCode } from '@utils/languageUtils';
import { isRichTextEmpty, sortByTimestamp } from '@utils/modelUtils';
import { journeyRoute } from '@utils/routeUtils';
import { consultEndsInFuture, consultStartsInFuture, convertTZ, timezoneAbbreviation } from '@utils/timeUtils';

type Props = {
  consult: GetJourneyWithExpertAndConsultsQuery['journey']['consults'][0];
  journey: GetJourneyWithExpertAndConsultsQuery['journey'];
};

const ExpertSessionCard = ({ consult, journey }: Props) => {
  const history = useHistory();
  const intl = useIntl();
  const myExpert = useMyExpert();
  const extraInfoOpener = useOpener();
  const editConsultOpener = useOpener();
  const consultMoveOpener = useOpener();
  const newJourneyModalOpener = useOpener();
  const cancelConsultOpener = useOpener();
  const rescheduleOpener = useOpener();
  const newJourneyModalRef = useRef<ModalElement>(null);
  const sanitizedNotes = useSanitizeHtml(consult.notes, journey.id, consult);

  const differentTimezoneRoles = journey.clientRoles.filter(role => role.client.timezone !== myExpert?.timezone);

  const openUrlLink = () => {
    if (consult.joinUrl) {
      window.open(consult.joinUrl, '_blank', 'noopener');
    }
  };

  const handleNewJourneyError = useCallback(() => {
    if (newJourneyModalRef.current) {
      newJourneyModalRef.current.scrollToTop();
    }
  }, [newJourneyModalRef]);

  const handleExpertCancelConsult = useCallback(() => {
    if (myExpert && !consultStartsInFuture(consult)) {
      cancelConsultOpener.open();
    } else if (consultStartsInFuture(consult)) {
      const url = journeyRoute({ journeyId: journey.id, linkAfterJourney: `/consults/${consult.id}/cancel`, myExpert });
      history.push(url);
    } else {
      // This means a client is attempting to cancel a consult that started in the past. That shouldn't
      // be possible because we hide the cancel link when the time passes.
    }
  }, [cancelConsultOpener, consult, history, journey.id, myExpert]);

  const handleCopyPhoneNumber = useCallback(() => {
    if (consult.phoneNumber?.number) {
      Clipboard.write({ string: consult.phoneNumber.number });
    }
  }, [consult.phoneNumber]);

  const isInitialConsult = (consult: Props['consult']) => {
    if (journey.initialConsultReport.consult?.id === consult.id) return true;

    const validConsults = sortByTimestamp(
      journey.consults.filter(
        consult => !consult.canceled && isPast(consult.startTime) && !consult.consultReport?.published
      ) ?? [],
      consult => consult.startTime
    );

    if (validConsults.length > 0 && validConsults[0].id === consult.id && !journey.initialConsultReport.consult?.id) {
      return true;
    }

    return false;
  };

  return (
    <div className={styles.sessionCard} data-test="session-card">
      <div className={styles.sessionCardTop}>
        <div className={styles.expertAndTime}>
          <ExpertAvatar expert={consult.expert} size={36} />
          <div>
            <BodyCopyBold color="primary700">
              <DateStamp value={consult.startTime} />
            </BodyCopyBold>
            <div>
              <BodyCopy color="primary700">
                <TimeRange endTime={consult.endTime} startTime={consult.startTime} />
              </BodyCopy>
              {differentTimezoneRoles?.map(role => {
                if (role.client.timezone) {
                  return (
                    <div className={styles.timezone} key={role.id}>
                      <BodyCopy color="neutral500">
                        {convertTZ(consult.startTime, role.client.timezone)}
                        <Nbsp />
                        {timezoneAbbreviation(role.client.timezone)}
                        <Nbsp />
                        <FormattedMessage id="dictionary.punctuation.dash" />
                        <Nbsp />
                        {role.client.firstName}
                      </BodyCopy>
                    </div>
                  );
                }
                return null;
              })}
            </div>
          </div>
        </div>
        <div className={styles.expertContact}>
          {consult.canceled ? (
            <div>
              <BodyCopy color="error500">
                <FormattedMessage id="pages.expertSection.canceled" />
                {consult.cancelationReason && (
                  <>
                    <Nbsp />
                    <FormattedMessage id="dictionary.punctuation.dash" />
                    <Nbsp /> <FormattedMessage id={`models.consult.${consult.cancelationReason}`} />
                  </>
                )}
              </BodyCopy>
            </div>
          ) : consultStartsInFuture(consult) ? (
            <div className={styles.consultItems}>
              <BodyCopy color="success500">
                <FormattedMessage id="pages.expertSection.scheduled" />
              </BodyCopy>
              {consult.contactMethod === ContactMethodEnum.PHONE ? (
                <>
                  <div className={`${styles.contactMethodCircle} ${styles.phone}`}>
                    <Icon
                      ariaLabelKey="accessibility.ariaLabels.contactMethod"
                      className={styles.contactMethodIcon}
                      icon={'phone'}
                    />
                  </div>
                  <AlertPopover textKey="dictionary.copied">
                    <div className={styles.contactMethodNumber} onClick={handleCopyPhoneNumber}>
                      <BodyCopy color="primary700">{consult.phoneNumber?.formattedNumber}</BodyCopy>
                    </div>
                  </AlertPopover>
                </>
              ) : (
                <>
                  <PrimaryButton onClick={openUrlLink} size="small" textKey="pages.expertSection.join" />
                  <div className={`${styles.contactMethodCircle} ${styles.video}`}>
                    <Icon
                      ariaLabelKey="accessibility.ariaLabels.contactMethod"
                      className={styles.contactMethodIcon}
                      icon={'video'}
                    />
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className={styles.consultItems}>
              <BodyCopy color="primary700">
                <FormattedMessage id={`models.duration.${consult.durationType}`} />
              </BodyCopy>
              <ActionIconButton
                ariaLabelKey="accessibility.ariaLabels.goToReport"
                hoverContent={intl.formatMessage({ id: 'pages.expertSection.goToReport' })}
                icon="file"
                routerLink={
                  isInitialConsult(consult)
                    ? journey.initialConsultReport.published
                      ? journeyRoute({
                          journeyId: consult.journey.id,
                          linkAfterJourney: `/initial-consult-report`,
                          myExpert
                        })
                      : journeyRoute({
                          journeyId: consult.journey.id,
                          linkAfterJourney: `/initial-consult-report/edit/${consult.id}`,
                          myExpert
                        })
                    : journeyRoute({
                        journeyId: consult.journey.id,
                        linkAfterJourney: `/consults/${consult.id}/consult-report`,
                        myExpert
                      })
                }
              />
            </div>
          )}
        </div>
        <div className={styles.consultItems + ' align-right'}>
          <div className={styles.clientInvites}>
            {consult.invites.map(invite => (
              <div key={invite.id}>
                <BodyCopy color="primary700">{invite.client?.fullName ?? invite.careTeamInvite?.email}</BodyCopy>
              </div>
            ))}
          </div>
        </div>
        <div className={styles.consultItems}>
          <div className={styles.iconSpacingContainer}>
            {(!isRichTextEmpty(consult.notes ?? '') || consult.languageCode || consult.cancelationNote) && (
              <ActionIconButton
                ariaLabelKey="accessibility.ariaLabels.toggle"
                color="secondary700"
                hoverContent={intl.formatMessage({ id: 'pages.expertSection.extraInfo' })}
                icon="circleExplamation"
                onClick={extraInfoOpener.toggle}
              />
            )}
          </div>
          <div className={styles.iconSpacingContainer}>
            {journey.initialConsultReport.consult?.id !== consult.id && (
              <ActionIconButton
                ariaLabelKey="models.consult.moveToAnotherJourney"
                hoverContent={intl.formatMessage({ id: 'models.consult.moveToAnotherJourney' })}
                icon="peopleArrows"
                onClick={consultMoveOpener.toggle}
              />
            )}
          </div>
          <div className={styles.iconSpacingContainer}>
            {consultEndsInFuture(consult) && (
              <ActionIconButton
                ariaLabelKey="pages.expertSection.reschedule"
                hoverContent={intl.formatMessage({ id: 'pages.expertSection.reschedule' })}
                icon="calendar"
                onClick={rescheduleOpener.toggle}
              />
            )}
          </div>
          <div className={styles.iconSpacingContainer}>
            {!consult.canceled && (
              <ActionIconButton
                ariaLabelKey="pages.consults.reschedule.updated.cancel"
                hoverContent={intl.formatMessage({ id: 'pages.consults.reschedule.updated.cancel' })}
                icon="calendarXmark"
                onClick={handleExpertCancelConsult}
              />
            )}
          </div>
          <div className={styles.iconSpacingContainer}>
            <ActionIconButton
              ariaLabelKey="accessibility.ariaLabels.edit"
              hoverContent={intl.formatMessage({ id: 'dictionary.edit' })}
              icon="edit"
              onClick={editConsultOpener.toggle}
            />
          </div>
        </div>
      </div>
      <ExpandableSection isOpen={extraInfoOpener.isOpen} key="extra-info-session">
        <div className={styles.sessionCardBottom}>
          {!isRichTextEmpty(consult.notes ?? '') && (
            <>
              <BodyCopyBold color="neutral700">
                <FormattedMessage id="forms.consult.notesLabel" />
              </BodyCopyBold>
              <BodyCopy color="neutral500">{sanitizedNotes}</BodyCopy>
            </>
          )}
          {consult.languageCode && (
            <>
              <div className={styles.infoSection}>
                <BodyCopyBold color="neutral700">
                  <FormattedMessage id="forms.consult.languageCodeLabel" />
                </BodyCopyBold>
              </div>
              <BodyCopy color="neutral500">
                <TextWithIcon icon="commentAltPlus" iconFirst text={languageNameForCode(consult.languageCode)} />
              </BodyCopy>
            </>
          )}
          {consult.cancelationNote && (
            <>
              <div className={styles.infoSection}>
                <BodyCopyBold color="neutral700">
                  <FormattedMessage id="forms.consultCancel.noteLabel" />
                </BodyCopyBold>
              </div>
              <BodyCopy color="neutral500">
                <BodyCopy color="neutral500">{consult.cancelationNote}</BodyCopy>
              </BodyCopy>
            </>
          )}
        </div>
      </ExpandableSection>
      <ExpandableSection isOpen={rescheduleOpener.isOpen} key="reschedule-session">
        <div className={styles.expertReschedule}>
          <ExpertRescheduleSession consult={consult} journey={journey} />
        </div>
      </ExpandableSection>
      <Modal opener={editConsultOpener} title={intl.formatMessage({ id: 'models.consult.editConsult' })}>
        {() => (
          <>
            <div className={styles.intro}>
              <BodyCopyBold>
                <FormattedMessage id="models.consult.dateAndTime" />
              </BodyCopyBold>
            </div>
            <div>
              <BodyCopy>
                <div>
                  <DateStamp value={consult.startTime} />
                </div>
                <div>
                  <TimeRange endTime={consult.endTime} startTime={consult.startTime} />
                </div>
              </BodyCopy>
            </div>

            <ConsultUpdateForm
              consult={consult}
              journey={journey}
              onCancel={editConsultOpener.close}
              opener={editConsultOpener}
            />
          </>
        )}
      </Modal>
      <Modal opener={consultMoveOpener} title={intl.formatMessage({ id: 'models.consult.moveToAnotherJourney' })}>
        {() => (
          <>
            <div>
              <BodyCopyBold>
                <FormattedMessage id="models.consult.consultFor" />
              </BodyCopyBold>
            </div>
            <div className={styles.consultFor}>
              {consult.invites
                .filter(invite => invite.client)
                .map((invitee, index) => (
                  <BodyCopy key={invitee.client?.id}>
                    {invitee.client?.fullName}
                    {index < consult.invites.length - 1 && (
                      <>
                        <FormattedMessage id="dictionary.punctuation.comma" />
                        <Nbsp />
                      </>
                    )}
                  </BodyCopy>
                ))}
            </div>
            <div className={styles.reschedule}>
              <div>
                <FormattedMessage id="models.consult.newJourney" />
              </div>
              <Hyperlink onClick={newJourneyModalOpener.open} onKeyUp={clickCurrentTarget} tabIndex={0}>
                <FormattedMessage id="models.consult.createNewJourney" />
              </Hyperlink>
            </div>
            {journey.owner.id && (
              <ConsultMoveForm
                clientId={journey.owner.id}
                consult={consult}
                journeyId={journey.id}
                onCancel={consultMoveOpener.close}
                onSuccess={consultMoveOpener.close}
              />
            )}
          </>
        )}
      </Modal>
      <Modal
        opener={newJourneyModalOpener}
        ref={newJourneyModalRef}
        title={intl.formatMessage({ id: 'pages.expert.journeys.newJourneyModal.title' })}
      >
        {({ modalDisplayed }) => {
          if (journey.owner.id) {
            return (
              <NewJourneyForm
                autofocus={modalDisplayed}
                clientId={journey.owner.id}
                onCancel={newJourneyModalOpener.close}
                onError={handleNewJourneyError}
                onSuccess={newJourneyModalOpener.close}
              />
            );
          }
          return <></>;
        }}
      </Modal>
      <Modal opener={cancelConsultOpener} title={intl.formatMessage({ id: 'models.consult.whyCancel' })}>
        {() => (
          <ConsultCancelForm
            consultId={consult.id}
            onCancel={cancelConsultOpener.close}
            onSuccess={cancelConsultOpener.close}
          />
        )}
      </Modal>
    </div>
  );
};

export default ExpertSessionCard;
